import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  HashratePipe,
  ModalData,
  ModalRef,
  TableColumnItem,
  TableComponent,
} from '@ui-lib/components';
import { getGeneralTableColumnsConfig } from './overview-dashboard-modal.constants';
import {
  FacilityMetricTableRow,
  IOverviewDashboardModalData,
} from './overview-dashboard-modal.model';
import { LoadingState } from '../../../../../shared/types/loading.type';
import { FormattedCountPipe } from '../../../../../shared/pipes/formatted-count.pipe';
import { SolHashratePipe } from '../../../../../shared/pipes/sol-hashrate.pipe';
import { FormattedTemperaturePipe } from '../../../../../shared/pipes/formatted-temperature.pipe';
import { OverviewDashboardModalService } from './overview-dashboard-modal.service';
import { PowerPipe } from '../../../../../shared/pipes/power.pipe';
import { map, shareReplay, startWith } from 'rxjs';
import { OverviewDashboardSummary } from '../overview-dashboard.model';

@Component({
  standalone: true,
  selector: 'mon-overview-dashboard-modal',
  templateUrl: './overview-dashboard-modal.component.html',
  styleUrls: ['./overview-dashboard-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    TableComponent,
    AsyncPipe,
    HashratePipe,
    FormattedCountPipe,
    SolHashratePipe,
    FormattedTemperaturePipe,
    PowerPipe,
  ],
  providers: [OverviewDashboardModalService],
})
export class OverviewDashboardModalComponent extends ModalData<IOverviewDashboardModalData> {
  private readonly modalRef = inject(ModalRef<void>);
  protected readonly overviewDashboardModalService = inject(OverviewDashboardModalService);

  protected readonly facilityMetrics$ = this.overviewDashboardModalService.facilityMetrics$.pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  protected readonly summary$ = this.facilityMetrics$.pipe(
    map((rows) => this.createSummary(rows)),
    startWith(this.modalData.summary),
  );

  protected readonly columns: TableColumnItem[] = getGeneralTableColumnsConfig();
  protected readonly LoadingState = LoadingState;

  public close(): void {
    this.modalRef.close();
  }

  private createSummary(rows: FacilityMetricTableRow[]): OverviewDashboardSummary | null {
    if (rows.length === 0) {
      return this.modalData.summary;
    }

    const activeDeviceCount = rows.reduce((sum, row) => sum + row.activeCount, 0);
    const freeDeviceCount = rows.reduce((sum, row) => sum + row.idleCount, 0);
    const averageTemperatureWeight = rows.reduce((sum, row) => sum + row.activeCount, 0);
    const averageTemperature =
      averageTemperatureWeight > 0
        ? rows.reduce((sum, row) => sum + row.averageTemperature * row.activeCount, 0) /
          averageTemperatureWeight
        : (this.modalData.summary?.averageTemperature ?? 0);

    return {
      totalDeviceCount: activeDeviceCount + freeDeviceCount,
      activeDeviceCount,
      freeDeviceCount,
      totalHashrate: rows.reduce((sum, row) => sum + row.totalHashrate, 0),
      totalSolHashrate: rows.reduce((sum, row) => sum + row.totalSolHashrate, 0),
      totalPower: rows.reduce((sum, row) => sum + row.totalPower, 0),
      freePower: rows.reduce((sum, row) => sum + row.freePower, 0),
      averageTemperature,
    };
  }
}
